import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { BarChart3, Clock, Receipt, Ban, Activity, DollarSign, Stethoscope, FlaskConical, Pill, ShieldCheck, Timer } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid } from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatCard = ({ icon: Icon, label, value, color, testId }) => (
  <div className={`bg-white rounded-xl border border-gray-200 p-4 shadow-sm`} data-testid={testId}>
    <div className="flex items-center gap-3">
      <div className={`p-2 rounded-lg ${color}`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <div>
        <p className="text-xs text-gray-500 font-medium">{label}</p>
        <p className="text-xl font-bold text-gray-800">{value}</p>
      </div>
    </div>
  </div>
);

const fmtWait = (h) => {
  if (h == null) return '—';
  if (h < 1) return `${Math.round(h * 60)} min`;
  if (h >= 48) return `${(h / 24).toFixed(1)} days`;
  return `${h.toFixed(1)} hrs`;
};

const QUEUE_META = [
  { key: 'consultant', label: 'Doctor Queue', icon: Stethoscope, color: 'text-blue-600 bg-blue-50 border-blue-200', hint: 'Check-in → consultation start' },
  { key: 'lab', label: 'Laboratory', icon: FlaskConical, color: 'text-amber-600 bg-amber-50 border-amber-200', hint: 'Tests ordered → results uploaded' },
  { key: 'pharmacy', label: 'Pharmacy', icon: Pill, color: 'text-orange-600 bg-orange-50 border-orange-200', hint: 'Prescribed → dispensed' },
  { key: 'billing', label: 'Billing', icon: Receipt, color: 'text-green-600 bg-green-50 border-green-200', hint: 'Ready → claim billed' },
  { key: 'approvals', label: 'Acacia Office Approvals', icon: ShieldCheck, color: 'text-purple-600 bg-purple-50 border-purple-200', hint: 'Requested → decision' },
];

const WorkloadTable = ({ title, rows }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-4">
    <p className="text-sm font-semibold text-gray-700 mb-2">{title}</p>
    {(!rows || rows.length === 0) ? (
      <p className="text-xs text-gray-400">No activity</p>
    ) : (
      rows.map((r) => (
        <div key={r.username} className="flex items-center justify-between py-1 border-t border-gray-100 first:border-t-0 text-sm">
          <span className="text-gray-700">{r.username}</span>
          <span className="font-semibold text-gray-800">{r.count}</span>
        </div>
      ))
    )}
  </div>
);

export default function InsightsPanel({ authCfg, isSuperAdmin }) {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);

  const load = useCallback(async (d) => {
    try {
      const res = await axios.get(`${API}/workflow/analytics?days=${d}`, authCfg);
      setData(res.data);
    } catch (e) {
      setData(null);
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => { load(days); }, [days, load]);

  if (!data) {
    return <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-500">Loading workflow insights…</div>;
  }

  const chartData = data.per_day.map((d) => ({ ...d, date: d.date.slice(5) }));

  return (
    <div className="space-y-4" data-testid="insights-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-base font-bold text-gray-800 flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-teal-600" /> Workflow Insights — last {data.days} days
        </h2>
        <div className="flex gap-1">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={`px-3 py-1 rounded-lg text-sm font-medium ${days === d ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              data-testid={`insights-days-${d}`}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <StatCard icon={Activity} label="Total Visits" value={data.total_visits} color="bg-teal-600" testId="insights-total-visits" />
        <StatCard icon={Receipt} label="Billed" value={data.billed} color="bg-green-600" testId="insights-billed" />
        <StatCard icon={Clock} label="Avg Turnaround" value={data.avg_turnaround_hours != null ? `${data.avg_turnaround_hours}h` : '—'} color="bg-indigo-600" testId="insights-turnaround" />
        <StatCard icon={Ban} label="Cancelled" value={data.cancelled} color="bg-red-500" testId="insights-cancelled" />
        <StatCard icon={DollarSign} label="Billed Revenue (USD)" value={`$${Number(data.revenue_usd).toFixed(2)}`} color="bg-emerald-600" testId="insights-revenue" />
      </div>

      <Card className="border-gray-200 shadow-sm" data-testid="insights-queue-waits">
        <CardHeader className="p-4 pb-0">
          <CardTitle className="text-sm text-gray-700 flex items-center gap-2">
            <Timer className="w-4 h-4 text-teal-600" /> Average Wait Time per Queue
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {QUEUE_META.map(({ key, label, icon: Icon, color, hint }) => (
              <div key={key} className={`rounded-xl border p-3 ${color.split(' ').slice(1).join(' ')}`} data-testid={`queue-wait-${key}`}>
                <div className="flex items-center gap-2 mb-1">
                  <Icon className={`w-4 h-4 ${color.split(' ')[0]}`} />
                  <p className="text-xs font-semibold text-gray-700">{label}</p>
                </div>
                <p className="text-lg font-bold text-gray-800">{fmtWait(data.queue_wait_hours?.[key])}</p>
                <p className="text-[10px] text-gray-500">{hint}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="border-gray-200 shadow-sm">
        <CardHeader className="p-4 pb-0">
          <CardTitle className="text-sm text-gray-700">Visits per day (check-ins vs billed)</CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          <div style={{ width: '100%', height: 260 }}>
            <ResponsiveContainer>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend />
                <Bar dataKey="checked_in" name="Checked in" fill="#0d9488" radius={[3, 3, 0, 0]} />
                <Bar dataKey="billed" name="Billed" fill="#16a34a" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="bg-white rounded-xl border border-gray-200 p-4" data-testid="insights-lab-approvals">
          <p className="text-sm font-semibold text-gray-700 mb-2">Lab Approvals (over threshold)</p>
          <div className="flex gap-4 text-sm">
            <span className="text-green-700">Approved: <strong>{data.lab_approvals.approved}</strong></span>
            <span className="text-red-600">Rejected: <strong>{data.lab_approvals.rejected}</strong></span>
            <span className="text-amber-600">Pending: <strong>{data.lab_approvals.pending}</strong></span>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4" data-testid="insights-rx-approvals">
          <p className="text-sm font-semibold text-gray-700 mb-2">Prescription Approvals (over threshold)</p>
          <div className="flex gap-4 text-sm">
            <span className="text-green-700">Approved: <strong>{data.rx_approvals.approved}</strong></span>
            <span className="text-red-600">Rejected: <strong>{data.rx_approvals.rejected}</strong></span>
            <span className="text-amber-600">Pending: <strong>{data.rx_approvals.pending}</strong></span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="insights-workload">
        <WorkloadTable title="Check-ins (Reception)" rows={data.workload.reception} />
        <WorkloadTable title="Consultations" rows={data.workload.consultants} />
        <WorkloadTable title="Lab Tests Completed" rows={data.workload.lab} />
        <WorkloadTable title="Medications Dispensed" rows={data.workload.pharmacy} />
        <WorkloadTable title="Claims Billed" rows={data.workload.billing} />
      </div>

      {isSuperAdmin && data.per_hospital && (
        <div className="bg-white rounded-xl border border-gray-200 p-4" data-testid="insights-per-hospital">
          <p className="text-sm font-semibold text-gray-700 mb-2">Visits per Hospital</p>
          {data.per_hospital.length === 0 ? (
            <p className="text-xs text-gray-400">No visits in this period</p>
          ) : (
            data.per_hospital.map((h) => (
              <div key={h.hospital_name} className="flex items-center justify-between py-1 border-t border-gray-100 first:border-t-0 text-sm">
                <span className="text-gray-700">{h.hospital_name}</span>
                <span className="font-semibold text-gray-800">{h.count}</span>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
